import { useState, useCallback } from 'react';
import type { AttachmentItem } from '@/api/solution';

type AttachmentStatus = {
  label: string;
  extraLabel?: string;
  color: string;
  icon: string;
  locked: boolean;
  cost?: number;
  hasVipPrivilege?: boolean;
};

interface AttachmentPanelProps {
  attachments: AttachmentItem[];
  checkAndDownload: (
    attachment: AttachmentItem,
    attachIndex: number,
    onSuccess: (url: string) => void,
    onRequireLogin: () => void,
  ) => Promise<void>;
  getAttachmentStatus: (attachment: AttachmentItem, attachIndex: number) => AttachmentStatus;
  onRequireLogin: () => void;
  onPdfPreview?: (previewUrl: string, name: string, downloadUrl?: string | null) => void;
}

const fileIconMap: Record<string, { icon: string; color: string; bg: string; label: string }> = {
  pdf: { icon: 'ri-file-pdf-2-line', color: '#ef4444', bg: '#fef2f2', label: 'PDF' },
  word: { icon: 'ri-file-word-2-line', color: '#0ea5e9', bg: '#e0f2fe', label: 'Word' },
  excel: { icon: 'ri-file-excel-2-line', color: '#10b981', bg: '#ecfdf5', label: 'Excel' },
  image: { icon: 'ri-image-line', color: '#f97316', bg: '#fff7ed', label: '图片' },
  other: { icon: 'ri-file-line', color: '#6b7280', bg: '#f3f4f6', label: '文件' },
};

function getFileInfo(type: string) {
  return fileIconMap[type] || fileIconMap.other;
}

function inferFileType(file: AttachmentItem): string {
  const source = `${file.name ?? ''} ${file.url ?? ''}`.toLowerCase();
  if (source.includes('.pdf')) return 'pdf';
  if (source.includes('.doc') || source.includes('.docx')) return 'word';
  if (source.includes('.xls') || source.includes('.xlsx') || source.includes('.csv')) return 'excel';
  if (source.includes('.png') || source.includes('.jpg') || source.includes('.jpeg') || source.includes('.gif') || source.includes('.webp')) {
    return 'image';
  }
  return 'other';
}

function openAttachmentUrl(url: string, fileName?: string) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

interface DownloadButtonProps {
  locked: boolean;
  status: AttachmentStatus;
  onDownload: () => void;
}

function DownloadButton({ locked, status, onDownload }: DownloadButtonProps) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = useCallback(() => {
    if (downloading) return;
    if (!locked) {
      setDownloading(true);
      setTimeout(() => setDownloading(false), 1200);
    }
    onDownload();
  }, [locked, onDownload, downloading]);

  if (downloading) {
    return (
      <button
        disabled
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0ea5e9]/10 text-[#0ea5e9] min-w-[72px] justify-center whitespace-nowrap"
      >
        <span className="w-3.5 h-3.5 border-2 border-[#0ea5e9]/40 border-t-[#0ea5e9] rounded-full animate-spin"></span>
        下载中
      </button>
    );
  }

  if (locked) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer whitespace-nowrap min-w-[72px] justify-center transition-all"
      >
        <i className="ri-lock-line text-xs"></i>
        {status.cost ? `${status.cost} 灵石` : '需权限'}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0ea5e9] text-white hover:bg-[#0284c7] cursor-pointer whitespace-nowrap min-w-[72px] justify-center transition-all"
    >
      <i className="ri-download-line text-xs"></i>
      下载
    </button>
  );
}

export default function AttachmentPanel({
  attachments, checkAndDownload, getAttachmentStatus, onRequireLogin, onPdfPreview
}: AttachmentPanelProps) {
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);

  const handleDownload = (att: AttachmentItem, idx: number) => {
    checkAndDownload(
      att as any,
      idx,
      (url) => {
        if (!url || String(url).trim() === '#') {
          setDownloadNotice(`「${att.name}」未配置下载地址`);
          setTimeout(() => setDownloadNotice(null), 3000);
          return;
        }
        openAttachmentUrl(url, att.name);
      },
      onRequireLogin,
    );
  };

  if (attachments.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {downloadNotice && (
        <div className="mx-5 mt-4 flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
          <i className="ri-information-line text-amber-500 shrink-0"></i>
          {downloadNotice}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-[#0ea5e9]/10 rounded-lg">
            <i className="ri-attachment-2 text-[#0ea5e9] text-base"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">附件下载</h3>
            <p className="text-xs text-gray-400 mt-0.5">{attachments.length} 个文件可下载</p>
          </div>
        </div>
        <span className="text-xs bg-[#0ea5e9]/10 text-[#0ea5e9] px-2.5 py-1 rounded-full font-medium">
          {attachments.filter((_, i) => !getAttachmentStatus(_ as any, i).locked).length} 免费
        </span>
      </div>

      {/* File List */}
      <ul className="divide-y divide-gray-50">
        {attachments.map((file, idx) => {
          const fileType = inferFileType(file);
          const fileInfo = getFileInfo(fileType);
          const status = getAttachmentStatus(file as any, idx);
          const isDownloaded = status.label === '已购';
          const isPdf = fileType === 'pdf';
          const previewUrl = file.fileObjectId ? `/api/v1/files/preview/${file.fileObjectId}` : file.url;
          const hasPdfUrl = Boolean(previewUrl);

          return (
            <li key={file.id || `${file.name}-${idx}`} className="group hover:bg-gray-50/70 transition-colors">
              <div className="flex items-center gap-3.5 px-5 py-3.5">
                {/* File icon */}
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0 relative"
                  style={{ backgroundColor: fileInfo.bg }}
                >
                  <i className={`${fileInfo.icon} text-lg`} style={{ color: fileInfo.color }}></i>
                  {isDownloaded && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#10b981] rounded-full border-2 border-white">
                      <i className="ri-check-line text-white" style={{ fontSize: '8px' }}></i>
                    </div>
                  )}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-800 truncate max-w-[180px]">{file.name}</p>
                    {isDownloaded && (
                      <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981] font-medium whitespace-nowrap shrink-0">
                        <i className="ri-checkbox-circle-fill text-xs"></i>已下载
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-gray-400">{file.size || '无'}</span>
                    <span className="text-xs text-gray-300">·</span>
                    <span className="text-xs font-medium" style={{ color: fileInfo.color }}>{fileInfo.label}</span>

                    {/* Permission badge */}
                    {!isDownloaded && (
                      <span
                        className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0"
                        style={{ backgroundColor: `${status.color}18`, color: status.color }}
                      >
                        <i className={`${status.icon} text-xs`}></i>
                        {status.label}
                      </span>
                    )}

                    {/* VIP extra badge */}
                    {status.hasVipPrivilege && !isDownloaded && (
                      <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-500 font-medium whitespace-nowrap shrink-0">
                        <i className="ri-vip-crown-line text-xs"></i>VIP免费
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {isPdf && onPdfPreview && hasPdfUrl && (
                    <button
                      onClick={() => onPdfPreview(previewUrl as string, file.name, file.downloadUrl || file.url)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
                      title="在线预览"
                    >
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                  )}
                  <DownloadButton
                    locked={status.locked}
                    status={status}
                    onDownload={() => handleDownload(file, idx)}
                  />
                </div>
              </div>

              {/* Progress bar decoration for locked items */}
              {status.locked && (
                <div className="mx-5 mb-3 -mt-1">
                  <div className="h-0.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: '0%',
                        backgroundColor: status.color,
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Footer tip */}
      <div className="px-5 py-3 bg-gray-50/60 border-t border-gray-50">
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <i className="ri-information-line text-[#0ea5e9]"></i>
          VIP 会员可免费下载所有带 VIP 特权标识的附件，灵石消耗仅需支付一次
        </p>
      </div>
    </div>
  );
}
